import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { AuthResponse, PublicUser } from '@waldermarch/shared';

import { ApiRequestError, api, getToken, setToken } from '@/utils/api';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<PublicUser | null>(null);
  const token = ref<string | null>(getToken());
  const loading = ref(false);
  const error = ref<string | null>(null);
  /** True until the initial "am I still signed in?" check has finished. */
  const initialising = ref(true);

  const isAuthenticated = computed(() => Boolean(user.value && token.value));

  function apply(response: AuthResponse): void {
    token.value = response.token;
    user.value = response.user;
    setToken(response.token);
    error.value = null;
  }

  async function register(
    username: string,
    email: string,
    password: string,
    region?: string,
  ): Promise<boolean> {
    loading.value = true;
    error.value = null;
    try {
      apply(await api.post<AuthResponse>('/auth/register', { username, email, password, region }));
      return true;
    } catch (err) {
      error.value = err instanceof ApiRequestError ? err.message : 'Registration failed.';
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function login(email: string, password: string): Promise<boolean> {
    loading.value = true;
    error.value = null;
    try {
      apply(await api.post<AuthResponse>('/auth/login', { email, password }));
      return true;
    } catch (err) {
      error.value = err instanceof ApiRequestError ? err.message : 'Sign-in failed.';
      return false;
    } finally {
      loading.value = false;
    }
  }

  /** Restore a session from a stored token, if the server still honours it. */
  async function restore(): Promise<void> {
    if (!token.value) {
      initialising.value = false;
      return;
    }
    try {
      user.value = await api.get<PublicUser>('/auth/me');
    } catch {
      // The in-memory world restarted, or the token expired. Either way: out.
      token.value = null;
      user.value = null;
      setToken(null);
    } finally {
      initialising.value = false;
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch {
      // Logging out locally is what matters; the token is stateless anyway.
    }
    user.value = null;
    token.value = null;
    setToken(null);
  }

  function clear(): void {
    user.value = null;
    token.value = null;
    setToken(null);
  }

  return {
    user,
    token,
    loading,
    error,
    initialising,
    isAuthenticated,
    register,
    login,
    restore,
    logout,
    clear,
  };
});
